import { Link } from '@tanstack/react-router';
import { useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useOperator } from '../../lib/operator-context';
import styles from '../../styles/operator.module.css';
import { useAuth } from '../../lib/auth-context';
import {
  LayoutDashboard,
  Search,
  Calendar,
  BookOpen,
  Clock,
  UserX,
  ShoppingCart,
  Truck,
  CreditCard,
  MessageSquare,
  FileEdit,
  Users,
  Star,
  ClipboardList,
  Settings,
  Lock,
  LockOpen,
  ShieldOff,
  ShieldCheck,
  LogOut,
  GitBranch,
  HeartPulse,
  Plug,
  Rocket,
  Package,
  UserCog,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface NavItem {
  name: string;
  path: string;
  icon: LucideIcon;
  visible: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

export default function OperatorSidebar() {
  const { pathname } = useLocation();
  const { operator, permissions } = useOperator();
  const { logout } = useAuth();
  const { t } = useTranslation();

  const isActive = (path: string) => {
    const p = pathname.replace(/\/$/, '') || '/';
    if (path === '/operator/dashboard') {
      return p === '/operator' || p === '/operator/dashboard';
    }
    return p === path || p.startsWith(path + '/');
  };

  const sections: NavSection[] = [
    {
      label: t('nav.sections.operations'),
      items: [
        {
          name: t('nav.dashboard'),
          path: '/operator/dashboard',
          icon: LayoutDashboard,
          visible: true,
        },
        {
          name: t('nav.calendar'),
          path: '/operator/calendar',
          icon: Calendar,
          visible: true,
        },
        {
          name: t('nav.bookings'),
          path: '/operator/bookings',
          icon: BookOpen,
          visible: true,
        },
        {
          name: t('nav.staff', 'Staff'),
          path: '/operator/staff',
          icon: UserCog,
          visible: true,
        },
        {
          name: t('nav.waitlist'),
          path: '/operator/waitlist',
          icon: Clock,
          visible: true,
        },
        {
          name: t('nav.noShows'),
          path: '/operator/no-shows',
          icon: UserX,
          visible: true,
        },
      ],
    },
    {
      label: t('nav.sections.commerce'),
      items: [
        {
          name: t('nav.orders'),
          path: '/operator/orders',
          icon: ShoppingCart,
          visible: true,
        },
        {
          name: t('nav.deliveries'),
          path: '/operator/deliveries',
          icon: Truck,
          visible: true,
        },
        {
          name: t('nav.productsAndServices', 'Products & Services'),
          path: '/operator/catalog',
          icon: Package,
          visible: true,
        },
        {
          name: t('nav.payments'),
          path: '/operator/payments',
          icon: CreditCard,
          visible: true,
        },
      ],
    },
    {
      label: t('nav.sections.messaging'),
      items: [
        {
          name: t('nav.conversations'),
          path: '/operator/conversations',
          icon: MessageSquare,
          visible: permissions?.canViewConversations ?? false,
        },
      ],
    },
    {
      label: t('nav.sections.whatsapp'),
      items: [
        {
          name: t('nav.onboarding', 'Onboarding'),
          path: '/operator/onboarding',
          icon: Rocket,
          visible: true,
        },
        {
          name: t('nav.templates'),
          path: '/operator/templates',
          icon: FileEdit,
          visible: true,
        },
        {
          name: t('nav.flows'),
          path: '/operator/flows',
          icon: GitBranch,
          visible: true,
        },
        {
          name: t('nav.phoneHealth'),
          path: '/operator/phone-health',
          icon: HeartPulse,
          visible: true,
        },
      ],
    },
    {
      label: t('nav.sections.customers'),
      items: [
        {
          name: t('nav.customers'),
          path: '/operator/customers',
          icon: Users,
          visible: permissions?.canSearchCustomers ?? false,
        },
        {
          name: t('nav.reviews'),
          path: '/operator/reviews',
          icon: Star,
          visible: true,
        },
      ],
    },
    {
      label: t('nav.sections.admin'),
      items: [
        {
          name: t('nav.integrations'),
          path: '/operator/integrations',
          icon: Plug,
          visible: true,
        },
        {
          name: t('nav.compliance'),
          path: '/operator/compliance',
          icon: ShieldCheck,
          visible: true,
        },
        {
          name: t('nav.auditLogs'),
          path: '/operator/audit-logs',
          icon: ClipboardList,
          visible: permissions?.canViewAuditLogs ?? false,
        },
        {
          name: t('nav.settings'),
          path: '/operator/settings',
          icon: Settings,
          visible: permissions?.canManageOperators ?? false,
        },
      ],
    },
  ];

  // Filter out invisible items; drop sections with no visible items
  const visibleSections = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item.visible),
    }))
    .filter((section) => section.items.length > 0);

  // Role badge colors
  const roleBadgeColors: Record<string, string> = {
    VIEWER: '#6b7280',
    SUPPORT: '#3b82f6',
    SUPERVISOR: '#8b5cf6',
    ADMIN: '#ef4444',
    COMPLIANCE: '#10b981',
  };

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <h1>{t('nav.appName', 'Domarcai')}</h1>
        <p>{t('nav.operatorPortal', 'Operator Portal')}</p>
      </div>

      {operator && (
        <div className={styles.operatorInfo}>
          <span className={styles.operatorName}>{operator.name}</span>
          <span
            className={styles.roleBadge}
            style={{ backgroundColor: roleBadgeColors[operator.role] || '#6b7280' }}
          >
            {operator.role}
          </span>
        </div>
      )}

      <nav className={styles.sidebarNav}>
        {visibleSections.map((section, idx) => (
          <div key={section.label}>
            {idx > 0 && <div className={styles.sidebarDivider} />}
            <div className={styles.sidebarSectionLabel}>{section.label}</div>
            {section.items.map((item) => {
              const IconComponent = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`${styles.sidebarLink} ${isActive(item.path) ? styles.active : ''}`}
                >
                  <IconComponent className={styles.sidebarIcon} size={18} />
                  <span className={styles.sidebarLabel}>{item.name}</span>
                </Link>
              );
            })}
          </div>
        ))}

        {/* AI Search pinned as utility at bottom */}
        <div className={styles.sidebarDivider} />
        <Link
          to="/operator/ai-search"
          className={`${styles.sidebarLink} ${isActive('/operator/ai-search') ? styles.active : ''}`}
        >
          <Search className={styles.sidebarIcon} size={18} />
          <span className={styles.sidebarLabel}>{t('nav.aiSearch')}</span>
        </Link>
      </nav>

      <div className={styles.sidebarFooter}>
        <div className={styles.permissionHints}>
          {permissions?.canViewFullPII ? (
            <span className={styles.piiIndicator} title={t('nav.fullPii', 'Full PII')}>
              <LockOpen size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              {t('nav.fullPii', 'Full PII')}
            </span>
          ) : permissions?.canViewConversations ? (
            <span className={styles.piiIndicator} title={t('nav.maskedPii', 'Masked PII')}>
              <Lock size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              {t('nav.maskedPii', 'Masked PII')}
            </span>
          ) : (
            <span className={styles.piiIndicator} title={t('nav.noPii', 'No PII')}>
              <ShieldOff size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              {t('nav.noPii', 'No PII')}
            </span>
          )}
        </div>
        <button className={styles.sidebarLogoutButton} onClick={logout}>
          <LogOut size={14} />
          <span>{t('common.logout')}</span>
        </button>
        <p>{t('nav.version', 'Operator Dashboard v1.0')}</p>
      </div>
    </aside>
  );
}
